import { useEffect, useMemo, useState } from "react"

import { useLazyGetAlertsQuery } from "store/api/alertsApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconEye } from "assets/svg/icon-eye.svg"

import { convertAlertsToTableRows } from "utils/convertData"
import { isMobileDevice } from "utils/deviceDetection"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import { useAlertViewDetails } from "hooks/useAlertViewDetails"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AlertsActions } from "components/common/AlertsActions"
import { ContentWrapper } from "components/common/ContentWrapper"
import ExportButton from "components/common/ExportButton"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import TableWrapper from "components/table/TableWrapper"

import { alertsTableHeaderCells } from "./AlertsTableHeaderCells"

import styles from "./index.module.scss"

interface AlertsPageContentProps {
  alertsList: ApiTypes.Model.Alert[]
  isFetching: boolean
  meta: ApiTypes.Model.Pagination | undefined
  onRefreshAlerts: () => void
}

export const AlertsPageContent = ({ alertsList, isFetching, meta, onRefreshAlerts }: AlertsPageContentProps) => {
  const [selectedAlerts, setSelectedAlerts] = useState<ApiTypes.Model.Alert[]>([])
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const [isHeaderChecked, setIsHeaderChecked] = useState(false)
  const { urlQueryParams } = useUrlQueryParams()
  const {
    selectedDevice,
    monitoringReportAnnotations,
    handleViewDetails,
    clearMonitoringReportAnnotations,
    closeStatsModal,
  } = useAlertViewDetails()

  useEffect(() => {
    triggerGetTableColumns({ list_name: "contractor_alerts_list" })
  }, [])

  useEffect(() => {
    setIsHeaderChecked(allAlertsSelected())
  }, [alertsList, selectedAlerts])

  const allAlertsSelected = () => {
    if (alertsList.length === 0) return false
    return alertsList.every(item => selectedAlerts.findIndex(alert => alert.id === item.id) > -1)
  }

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(alertsTableHeaderCells, columnsData?.data?.config)
        : alertsTableHeaderCells,
    [columnsData],
  )

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta,
    fileName: "contractor_alerts",
    convertData: convertAlertsToTableRows,
    useLazyQuery: useLazyGetAlertsQuery,
  })

  const onSelectAlert = (id: number) => {
    if (selectedAlerts.some(item => item.id === id)) {
      setSelectedAlerts(selectedAlerts.filter(item => item.id !== id))
    } else {
      setSelectedAlerts([...selectedAlerts, alertsList.find(item => item.id === id)!])
    }
  }

  const handleSuccess = () => {
    onRefreshAlerts()
    setSelectedAlerts([])
  }

  const toggleAllAlerts = () => {
    if (isHeaderChecked) {
      setSelectedAlerts([])
      setIsHeaderChecked(false)
    } else {
      setSelectedAlerts(alertsList)
      setIsHeaderChecked(true)
    }
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    if (rowData.type?.value !== "Efficiency Issue") {
      return {
        className: "",
        actionButtons: [],
      }
    }

    return {
      className: styles.messageTemplateButtonContainer,
      actionButtons: [
        {
          icon: <IconEye />,
          tooltip: "View on chart",
          tooltipPosition: "top-start" as const,
          hidden: rowData.rowSettings.isEnable === false,
          handler: () => handleViewDetails(rowData),
        },
      ],
    }
  }

  return (
    <ContentWrapper title="Alerts History">
      <nav className={styles.tableNavWrapper}>
        <AlertsActions
          selectedAlerts={selectedAlerts}
          onSuccess={handleSuccess}
          selectedDevice={selectedDevice}
          monitoringReportAnnotations={monitoringReportAnnotations}
          onClearMonitoringReportAnnotations={clearMonitoringReportAnnotations}
          onCloseStatsModal={closeStatsModal}
        />
        <div className={styles.tableNavButtonsWrapper}>
          <EditColumnsButton tableName="contractor_alerts_list" tableHeaderCells={tableHeaderCells} />
          <ExportButton onClick={handleExport} disabled={meta?.total === 0} />
        </div>
      </nav>
      <TableWrapper
        headerCells={tableHeaderCells}
        columnsLoaded={!!columnsData}
        data={convertAlertsToTableRows(alertsList)}
        isLoading={isFetching}
        onCheckboxChange={onSelectAlert}
        currentItemIDs={selectedAlerts.map(item => item.id)}
        getRowActions={getRowActions}
        onHeaderCheckboxChange={toggleAllAlerts}
        isHeaderChecked={isHeaderChecked}
        isMobileDevice={isMobileDevice()}
      />
      <Pagination meta={meta} />
    </ContentWrapper>
  )
}
